import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Stack,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useBuild } from '../../contexts/BuildContext';

const parseAddresses = (raw) =>
  raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

const ParamsTokenItemsV1 = ({ onParamsChange }) => {
  const { fetchCachedList } = useBuild();
  const [chain, setChain] = useState('');
  const [chainOptions, setChainOptions] = useState([]);
  const [chainsLoading, setChainsLoading] = useState(true);
  const [chainsError, setChainsError] = useState(null);
  const [addressesRaw, setAddressesRaw] = useState('');
  const [tradeLimit, setTradeLimit] = useState(10);

  useEffect(() => {
    const load = async () => {
      try {
        setChainsLoading(true);
        setChainsError(null);
        const data = await fetchCachedList('/search/frequent_values/token-items/chain');
        const opts = data || [];
        setChainOptions(opts);
        if (opts.length > 0 && !chain) setChain(opts[0].id);
      } catch (err) {
        console.error('Failed to load chains:', err);
        setChainsError(err.message);
      } finally {
        setChainsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCachedList]);

  useEffect(() => {
    const addresses = parseAddresses(addressesRaw);
    if (!chain || addresses.length === 0) {
      onParamsChange(null);
      return;
    }
    const tokens = addresses.map((address) => ({ chain, address }));
    onParamsChange({ tokens, trade_limit: parseInt(tradeLimit, 10) || 10 });
  }, [chain, addressesRaw, tradeLimit, onParamsChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="caption" color="text.secondary">
            Pick a chain and paste one or more token contract addresses
            (one per line or comma-separated). The backend composes the
            Bigtable row key internally — no need to type
            <code> TOKEN#…#…#METADATA</code>.
          </Typography>

          {chainsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          ) : chainsError ? (
            <Alert severity="error">Failed to load chains: {chainsError}</Alert>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel>Chain</InputLabel>
              <Select
                value={chain}
                label="Chain"
                onChange={(e) => setChain(e.target.value)}
              >
                {chainOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.id} ({opt.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Token addresses"
            placeholder={'0x000000000000012def132e61759048be5b5c6033'}
            value={addressesRaw}
            onChange={(e) => setAddressesRaw(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            size="small"
            variant="outlined"
          />
          <TextField
            label="Trades per token"
            type="number"
            value={tradeLimit}
            onChange={(e) => setTradeLimit(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            inputProps={{ min: 1, max: 100 }}
            helperText="Most recent interesting_trades returned per item (default 10)."
          />
        </Stack>
      </Paper>
    </Box>
  );
};

export default ParamsTokenItemsV1;
